import React from "react";
import PropTypes from "prop-types";
import { Redirect, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { useOnboardingGate } from "../OnBoardingScreen/use-onboarding-gate";
import { isOnboardingScan } from "../OnBoardingScreen/session-keys";
import {
  canAccessDashboard,
  isContinueResumeTarget,
  resumeRedirect,
} from "../OnBoardingScreen/user-access";

// /onboarding and /onboarding/continue.
// Dashboard-ready users are sent to the app. Everyone else stays in onboarding
// (mid-scan) or is sent to their resume target from server step_status.
const OnboardingGuard = ({ children }) => {
  const session = useSelector((state) => state?.session);
  const location = useLocation();
  const midOnboarding = isOnboardingScan();
  const onContinue = location.pathname === "/onboarding/continue";
  const resuming =
    location.pathname === "/onboarding" && Boolean(location.state?.resume);

  // Resume routing only — mid-scan stays put; stale tokens are cleared when an
  // authenticated onboarding request returns 401.
  const needsServerStatus =
    Boolean(session?.auth) &&
    !session?.isLoading &&
    !canAccessDashboard() &&
    !midOnboarding &&
    !resuming;

  const { ready, redirectTo } = useOnboardingGate({
    authed: session?.auth,
    loading: session?.isLoading,
    needsServerStatus,
    onContinue,
  });

  if (session?.isLoading || (needsServerStatus && !ready)) {
    return null;
  }

  // Continue is only for signed-in users mid procurement/operations.
  if (!session?.auth) {
    if (onContinue) {
      return <Redirect to="/onboarding" />;
    }
    return <>{children}</>;
  }

  if (canAccessDashboard()) {
    return <Redirect to="/" />;
  }

  // Mid-questionnaire: stay on /onboarding (flow state is in sessionStorage).
  // Continue and one-shot resume entry are also allowed surfaces.
  if (midOnboarding || resuming) {
    return <>{children}</>;
  }

  if (onContinue && isContinueResumeTarget()) {
    return <>{children}</>;
  }

  if (redirectTo) {
    return <Redirect to={redirectTo} />;
  }

  return <Redirect to={resumeRedirect()} />;
};

OnboardingGuard.propTypes = {
  children: PropTypes.node,
};

export default OnboardingGuard;
